/*
===============================================================================================================
                                 Copyright 2025-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/*
  * This module manages the dynamic import and access to the lui-portal-utilities package.
  * It ensures that the package is only loaded when needed (e.g., in an iframe context),
  * and provides utility functions to interact with it.
*/

let portalUtilities = null;
let loadingPromise = null;

const loadPortalUtilities = async () => {
  if (portalUtilities) {
    return portalUtilities;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = import('lui-portal-utilities')
    .then((utils) => {
      portalUtilities = utils;
      return utils;
    })
    .catch((error) => {
      loadingPromise = null; // Reset so we can try again
      throw error;
    });

  return loadingPromise;
};

const isInIFrame = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.self !== window.parent;
};

// Initialize loading immediately when this module is imported
// but only if in an iframe context
if (typeof window !== 'undefined' && isInIFrame()) {
  loadPortalUtilities();
}

export const getPortalUtilities = () => portalUtilities;

export const waitForPortalUtilities = async () => {
  if (portalUtilities) {
    return Promise.resolve(portalUtilities);
  }
  return loadPortalUtilities();
};

const fetchUserPermissions = async () => {
  const utils = await waitForPortalUtilities();
  const permissionRequestMessage = new utils.AccessPermissionsRequestMessage({
    includePermissions: true,
    includePolicies: false,
  });
  const { requestDataFromPortal } = utils.PortalMessageService.getInstance();
  return requestDataFromPortal(permissionRequestMessage);
};

const fetchUserPreferences = async () => {
  const utils = await waitForPortalUtilities();
  const preferencesRequestMessage = new utils.UserPreferencesRequestMessage();
  const { requestDataFromPortal } = utils.PortalMessageService.getInstance();
  return requestDataFromPortal(preferencesRequestMessage);
};

export const isPortalUtilitiesLoaded = () => Boolean(portalUtilities);

export const fetchWithLuiUtils = async ({ url, params, method }) => {
  // Wait for utilities to be loaded if not already
  const utils = await waitForPortalUtilities();

  if (!isInIFrame()) {
    throw new Error('LUI utils can only be used within an iframe context');
  }

  let apiMethod;
  switch (method) {
    case 'POST':
      apiMethod = utils.postData;
      break;
    case 'GET':
      apiMethod = utils.fetchData;
      break;
    case 'PUT':
      apiMethod = utils.putData;
      break;
    case 'DELETE':
      apiMethod = utils.deleteData;
      break;
    case 'PATCH':
      apiMethod = utils.patchData;
      break;
    default:
      throw new Error(`LUI utils: unsupported method ${method}`);
  }

  return apiMethod(url, params);
};

export default {
  getUtils: getPortalUtilities,
  isLoaded: isPortalUtilitiesLoaded,
  isInIFrame,
  fetchWithLuiUtils,
  isWidgetModeActive: () => isPortalUtilitiesLoaded() && isInIFrame(),
  fetchUserPermissions,
  fetchUserPreferences,
};
